import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import api from '@/api'
import router from '@/router'

export interface User {
  user_id: string
  email: string
  phone: string
  role: 'admin_vendor' | 'sales_vendor' | 'customer'
  business_name: string
}

export interface LoginCredentials {
  email: string
  password: string
}

export interface RegisterData {
  email: string
  phone: string
  password: string
  business_name: string
  role: 'admin_vendor' | 'sales_vendor' | 'customer'
}

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(localStorage.getItem('auth_token'))
  const user = ref<User | null>(null)
  const loading = ref(false)
  const refreshInterval = ref<number | null>(null)
  let refreshPromise: Promise<boolean> | null = null


  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isAdminVendor = computed(() => user.value?.role === 'admin_vendor')
  const isSalesVendor = computed(() => user.value?.role === 'sales_vendor')
  const isCustomer = computed(() => user.value?.role === 'customer')
  const isVendor = computed(() => isAdminVendor.value || isSalesVendor.value)

  const extractApiError = (err: unknown, fallbackMessage: string): Record<string, unknown> => {
    if (typeof err === 'object' && err !== null) {
      const maybeError = err as { response?: { data?: unknown } }
      const responseData = maybeError.response?.data
      if (typeof responseData === 'object' && responseData !== null) {
        return responseData as Record<string, unknown>
      }
    }

    if (err instanceof Error && err.message) {
      return { error: err.message }
    }

    return { error: fallbackMessage }
  }

  // Token refresh function
  async function refreshToken() {
    if (!token.value) return false

    // Prevent multiple simultaneous refresh attempts
    if (refreshPromise) {
      return refreshPromise
    }

    refreshPromise = (async () => {
      try {
        const response = await api.post('/auth/refresh')
        const { token: newToken } = response.data
        
        token.value = newToken
        localStorage.setItem('auth_token', newToken)
        return true
      } catch (error: unknown) {
        logout()
        return false
      } finally {
        refreshPromise = null
      }
    })()

    return refreshPromise
  }

  // Setup automatic token refresh (refresh every 6 hours, token expires in 8 hours)
  function startTokenRefresh() {
    if (refreshInterval.value) {
      clearInterval(refreshInterval.value)
    }
    
    // Refresh token every 6 hours (21600000 ms = 6 * 60 * 60 * 1000)
    refreshInterval.value = window.setInterval(() => {
      if (token.value) {
        refreshToken()
      }
    }, 21600000) // 6 hours
  }

  function stopTokenRefresh() {
    if (refreshInterval.value) {
      clearInterval(refreshInterval.value)
      refreshInterval.value = null
    }
  }

  async function login(credentials: LoginCredentials) {
    loading.value = true
    try {
      const response = await api.post('/auth/login', credentials)
      const { token: authToken, user: userData } = response.data
      
      token.value = authToken
      user.value = userData
      localStorage.setItem('auth_token', authToken)
      
      // Start automatic token refresh
      startTokenRefresh()
      
      return response.data
    } catch (error: unknown) {
      throw extractApiError(error, 'Login failed')
    } finally {
      loading.value = false
    }
  }

  async function register(userData: RegisterData) {
    loading.value = true
    try {
      const response = await api.post('/auth/register', userData)
      return response.data
    } catch (error: unknown) {
      throw extractApiError(error, 'Registration failed')
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    loading.value = true
    try {
      if (token.value) {
        await api.post('/auth/logout')
      }
    } catch (error: unknown) {
      // Silent logout error
    } finally {
      // Stop token refresh interval
      stopTokenRefresh()
      
      token.value = null
      user.value = null
      localStorage.removeItem('auth_token')
      loading.value = false
      router.push('/login')
    }
  }

  async function fetchUser() {
    if (!token.value) {
      return
    }

    loading.value = true
    try {
      const response = await api.get('/auth/me')
      user.value = response.data.user
    } catch (error: unknown) {
      logout()
    } finally {
      loading.value = false
    }
  }

  async function forgotPassword(email: string) {
    loading.value = true
    try {
      const response = await api.post('/auth/forgot-password', { email })
      return response.data
    } catch (error: unknown) {
      throw extractApiError(error, 'Password reset failed')
    } finally {
      loading.value = false
    }
  }

  async function resetPassword(token: string, newPassword: string) {
    loading.value = true
    try {
      const response = await api.post('/auth/reset-password', {
        token,
        new_password: newPassword
      })
      return response.data
    } catch (error: unknown) {
      throw extractApiError(error, 'Password reset failed')
    } finally {
      loading.value = false
    }
  }

  async function verifyEmail(verificationToken: string) {
    loading.value = true
    try {
      const response = await api.get(`/auth/verify-email/${verificationToken}`)
      return response.data
    } catch (error: unknown) {
      throw extractApiError(error, 'Email verification failed')
    } finally {
      loading.value = false
    }
  }

  // Initialize auth state on store creation (prevent multiple calls)
  let isInitialFetch = false
  if (token.value && !user.value && !isInitialFetch) {
    isInitialFetch = true
    fetchUser().finally(() => {
      isInitialFetch = false
      // Start token refresh if user is already logged in
      if (token.value && user.value) {
        startTokenRefresh()
      }
    })
  }

  return {
    token,
    user,
    loading,
    isAuthenticated,
    isAdminVendor,
    isSalesVendor,
    isCustomer,
    isVendor,
    login,
    register,
    logout,
    fetchUser,
    refreshToken,
    startTokenRefresh,
    stopTokenRefresh,
    forgotPassword,
    resetPassword,
    verifyEmail
  }
})
